import { cn } from "@/lib/utils";
import { useTranslation } from "@repo/shared/i18n";

import { CreditIcon } from "./CreditIcon";

/**
 * "1 free AI credit ●" — the daily grant, with the coin attached.
 *
 * The icon rides with the phrase rather than being placed by each caller, so
 * the sentence stays one unit: it can't wrap between the noun and its coin, and
 * a caller can't accidentally show a coin next to the number-free variant's
 * wording in a way that reads as "one credit".
 *
 * The wording is `dailyLimitNoun` from `@repo/shared/credits`, moved into the
 * locale files: the number-free branch is its own key because a grant-less
 * phrase opens on a word, so only that one takes a capital when it starts a
 * sentence. Mobile still reads the English helper — see planCopy.test.ts.
 */
export const DailyLimitPhrase = ({
  limit,
  className,
  sentenceStart,
}: {
  limit?: number;
  className?: string;
  /** Set when the phrase opens a sentence — see dailyLimitNoun. */
  sentenceStart?: boolean;
}) => {
  const { t } = useTranslation("subscription");
  /*
    A limit of 0 (or a NaN off the wire) takes the number-free branch, same as
    the shared helper: "0 free AI credits every day" advertises the absence of
    the thing the bullet is selling.
  */
  const known = typeof limit === "number" && limit > 0;

  return (
    /*
      Plain inline, not inline-flex: a flex container centres its content in a
      box of its own, so the phrase sat proud of the baseline of the sentence
      around it. Ordinary inline text shares the line's baseline for free, and
      whitespace-nowrap is what keeps the coin from wrapping off the end.
    */
    <span className={cn("whitespace-nowrap", className)}>
      <span className="font-semibold text-foreground">
        {known
          ? t("credits.dailyLimitNoun", { count: limit })
          : t(
              sentenceStart
                ? "credits.dailyLimitNounUnknownStart"
                : "credits.dailyLimitNounUnknown",
            )}
      </span>{" "}
      <CreditIcon size="small" className="align-middle" />
    </span>
  );
};
